import { DatePipe } from "@angular/common";
import { Component, Injector } from "@angular/core";
import { UntypedFormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { BaseEntityComponent, BnLoadingService, messageLevel } from "@bndes/bndes-ux4/bndes-angular-utils";
import { BnConfirmDirective } from "@bndes/bndes-ux4/bn-confirm";
import { BnModalMessageService } from "@bndes/bndes-ux4/bn-modal-message";
import { BnUsuarioService, Usuario } from "@bndes/bndes-ux4/bn-usuario";
import moment from "moment";
import { Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { ROLE_GESTOR_ENVIO } from "src/app/constantes/permissao.constants";
import { CanComponentDeactivate } from "src/app/core/can-deactivate-guard.service";
import { AgenteFinanceiro } from "src/app/model/agente-financeiro.model";
import { TIPO_CLASSE_OPERACAO } from "src/app/model/nota.model";
import { IrregularidadeOperacaoGarantida } from "src/app/model/operacao-irregular.model";
import Utils from "src/app/shared/fgi-utils";
import { OcorrenciaValidacaoVO } from "src/app/shared/ocorrencia-validacao.vo";
import { PermissaoService } from 'src/app/shared/permissao.service';
import { RemoverAlertasService } from "src/app/shared/services/remover-alertas.service";
import { IrregularidadeService } from "../irregularidade.service";


@Component({
    templateUrl: './irregularidade-cadastro.component.html',
    standalone: false
})
export class IrregularidadeCadastroComponent
        extends BaseEntityComponent<IrregularidadeOperacaoGarantida> implements CanComponentDeactivate {
    
    public classesOperacao = TIPO_CLASSE_OPERACAO;
    private formIrregularidade:UntypedFormGroup = null;
    private agentesFinanceiros:AgenteFinanceiro[]=[];
    private ocorrencias:OcorrenciaValidacaoVO[] = [];
    private usuarioLogado:Usuario;
    private vc: BnConfirmDirective;
    private permissaoEdicao: Boolean;

    constructor(
        private activatedRoute:ActivatedRoute,
        private modalService: BnModalMessageService,
        private permissaoService: PermissaoService,
        private loadingService: BnLoadingService,
        protected injector: Injector,
        private usuarioService: BnUsuarioService,
        private irregularidadeService: IrregularidadeService,
        private datePipe:DatePipe,
        private removerAlertasService: RemoverAlertasService,
        ) {
        super(irregularidadeService);
        this.config.rotaLista = "irregularidade/pesquisa";
        this.config.bloquearTelaAoRecuperarModelo = true;
        this.agentesFinanceiros = this.activatedRoute.snapshot.data['agentes'];
        this.permissaoEdicao = false;
    }

    ngOnInit(){
        this.formIrregularidade = this.createReactiveForm();
        this.usuarioService.usuario$.subscribe(usuario => this.usuarioLogado = usuario);
        this.permissaoService.getPermissoesUsuarioAutenticado().subscribe(resp => {
            this.permissaoEdicao = resp[ROLE_GESTOR_ENVIO];
            this.preparaModelo().subscribe((irregularidade) => {
                this.setModel(irregularidade);
                this.preencheForm(irregularidade);
                if(irregularidade.id){
                    this.formIrregularidade.disable();
                }
            });
        });
    }

    preparaModelo(): Observable<IrregularidadeOperacaoGarantida> {
        return this.route.params.pipe(
            mergeMap((params) => {
                if (params['id']) {
                    return this.irregularidadeService.findById(params['id']);
                } else {
                    const novo: IrregularidadeOperacaoGarantida = this.irregularidadeService.getNewEntity();
                    novo.nomeUsuarioEnvio = this.usuarioLogado.nome;
                    novo.dataEnvio = moment().format(('YYYY-MM-DDTHH:mm:ss'));
                    novo.dataInicio = moment().format(('YYYY-MM-DD'));
                    super.editar();
                    return of(novo);
                }
            })
        );
    }

    createReactiveForm(): UntypedFormGroup {
       const form = this.formBuilder.group({            
            idAgenteFinanceiro: [{value: null},Validators.required],
            classeOperacao: [{value: null}],
            idOperacaoExterno:[{value: null}],
            contratoRepasse:[{value: null}],
            dataInicio:[{value: null, disabled: true}, [Validators.required]],            
            dataFim:[{value: null}],            
            motivo: [{value: null},Validators.compose([Validators.required, Validators.minLength(10), Validators.maxLength(100)])],
            mensagem: [{value: null},Validators.compose([Validators.required, Validators.minLength(10), Validators.maxLength(1000)])],
            dataEnvio: [{value: null, disabled: true}],
            nomeUsuarioEnvio:[{value:null,disabled: true}],
        });         
    
        form.get('classeOperacao').valueChanges.subscribe(sistema => {
            const contratoRepasse = form.get('contratoRepasse');
            const idOperacaoExterno = form.get('idOperacaoExterno');

            if (sistema === 'CL') {
                idOperacaoExterno.setValidators([Validators.pattern(/^[A-Za-z0-9_\-]+$/), 
                    Validators.minLength(1), Validators.maxLength(20), Validators.required]);
                if(this.modoEdicao) idOperacaoExterno.enable();
                contratoRepasse.disable();
                contratoRepasse.reset();
                contratoRepasse.clearValidators();

            } else {
                contratoRepasse.setValidators([Validators.pattern(/^[0-9]+$/), 
                    Validators.minLength(11), Validators.maxLength(11), Validators.required]);
                contratoRepasse.enable();
                idOperacaoExterno.disable();
                idOperacaoExterno.reset();
                idOperacaoExterno.clearValidators();
            }

            contratoRepasse.updateValueAndValidity();
            idOperacaoExterno.updateValueAndValidity();
        });

       return form;
    }

    salvar() {
        this.loadingService.bloqueiaTela();
        this.antesDeSalvar();
        this.atualizaModel();

        this.ocorrencias = [];
        this.removerAlertasService.removerTodos();
        this.irregularidadeService['save'](this.getModel()).subscribe(
            (notaRetornada) => {
                this.model = notaRetornada;
                this.aposSalvar();
                this.loadingService.liberaTela();
            },
            (error) => {
                console.log(error);
                this.ocorrencias = this.irregularidadeService.ocorrencias;
                this.loadingService.liberaTela();
            });
    }

    preencheForm(model:IrregularidadeOperacaoGarantida){
        this.formIrregularidade.patchValue({
            idAgenteFinanceiro: model.idAgenteFinanceiro ? model.idAgenteFinanceiro : null,
            classeOperacao: model.sistemaRepasse ? model.sistemaRepasse : 'CL',
            idOperacaoExterno: model.idOperacaoExterno,
            contratoRepasse: model.contratoRepasse,
            dataEnvio: moment(model.dataEnvio).format("DD/MM/YYYY HH:mm"),
            dataInicio: Utils.ajustaCampoDate(model.dataInicio),
            dataFim: Utils.ajustaCampoDate(model.dataFim),
            motivo: model.motivo,
            mensagem: model.mensagem,
            nomeUsuarioEnvio: model.nomeUsuarioEnvio
        });
    }

    habilitarEdicao() {
        
        this.formIrregularidade.get('dataInicio').enable();
        this.formIrregularidade.get('dataFim').enable();
        this.formIrregularidade.get('motivo').enable();
        this.formIrregularidade.get('mensagem').enable();
        super.editar();
    }

    atualizaModel(){
        const cadastro = this.formIrregularidade.value;
        if(cadastro.idAgenteFinanceiro){
            this.model.idAgenteFinanceiro = cadastro.idAgenteFinanceiro;
        }
        if(cadastro.idOperacaoExterno){
            this.model.idOperacaoExterno = cadastro.idOperacaoExterno;
            this.model.contratoRepasse =null;
        }
        if(cadastro.contratoRepasse){
            this.model.contratoRepasse = cadastro.contratoRepasse;
        }
        if(cadastro.motivo){
            this.model.motivo = cadastro.motivo;
        }
        if(cadastro.mensagem){
            this.model.mensagem = cadastro.mensagem;
        }
        if(cadastro.dataInicio){
            this.model.dataInicio = cadastro.dataInicio;
        }
        
        if(cadastro.dataEnvio){
            this.model.dataEnvio = this.model.dataEnvio;
        }
        this.model.sistemaRepasse = cadastro.classeOperacao=='CL' ? null : cadastro.classeOperacao;
        this.model.dataFim = cadastro.dataFim || null;
    }

    voltarParaLista() {
        this.removerAlertasService.removerTodos();
        super.voltarParaLista();
    }

    aposSalvar() {
        this.exibeMensagemSucesso();
        this.formIrregularidade.markAsPristine();
        this.formIrregularidade.disable();
        this.modoEdicao = false;
    }
    
    exibeMensagemSucesso(): void {
        this.messageService.addMessage({
            title: 'Sucesso',
            type: 'CRUD_OPERATION',
            level: messageLevel.SUCCESS,
            message: 'Irregularidade criada/atualizada com sucesso.'
        });
    }
        

    async verificaSePodeSair(): Promise<boolean> {
        const titulo = 'Confirmação';
        const mensagem = this.model.id!=null ? 'Descartar alterações realizadas na irregularidade ?' : 'Descartar novo cadastro de irregularidade ?';
        this.vc.tituloConfirm = 'Confirmação';

        try {
            const resultadoConfimacao = await this.modalService.abrirConfirm(titulo, mensagem).result;
            return resultadoConfimacao.value === 'ok';
        } catch (e) {
            return false;
        }
    }

    canDeactivate(): Promise<boolean> | boolean {
        if (!this.modoEdicao || this.formIrregularidade.pristine)
            return true;
        
        return this.verificaSePodeSair();
    }
}
